import { MergedServiceCodes, ServiceCode } from '@/shared/types';
import { RMPServiceCode } from '@/shared/types/serviceCodes';

import { FileType } from '@/helpers/fileHelper';

import { client, clientPublic } from '../core/client';
import {
  DeleteRequestParams,
  ERZPermit,
  PayloadCreateDraft,
  PayloadUpdateDraft,
  PayloadVerifySubmitAvailability,
  ResponseCreateDraft,
  ResponsePaymentDetails,
  ResponseUserWeapons,
  ResponseVerifySubmitAvailability,
  SaveAttachmentParams,
  SavedAttachment,
  ServiceCategory,
  ServiceSupportedCustomWeaponList,
  UserRequest,
  UserRequestDraft,
  UserService,
  DataResponse,
  ResponseArtifact,
  UserServiceExternal,
} from '../types';

export const services = {
  getAll: (): Promise<DataResponse<UserService[]>> => {
    return client.get('/services');
  },
  getExternal: (): Promise<DataResponse<UserServiceExternal[]>> => {
    return clientPublic.get('/external-services');
  },
  categories: (): Promise<DataResponse<ServiceCategory[]>> => {
    return clientPublic.get('/service-categories');
  },
  generatePdf: <In>(code: ServiceCode, payload: In): Promise<ResponseArtifact> => {
    const headers = new Headers();
    headers.set('Accept', FileType.PDF);
    return client.post(`/user-requests/${code}/generatePdf`, payload, headers);
  },
  submit: <In>(
    code: ServiceCode,
    payload: In,
    allowToProfileUpdate?: string,
  ): Promise<{ status: 'success'; id?: string }> => {
    const headers = new Headers();
    if (allowToProfileUpdate) {
      headers.set('App-Allow-Profile-Update', allowToProfileUpdate);
    }
    return client.post(`/user-requests/${code}/submitForm`, payload, headers);
  },
  attachReceipt: async (id: UserRequest['id'], receipt: string | File): Promise<void> => {
    if (typeof receipt === 'string') {
      return client.put(`/user-requests/${id}/attachments/receipt`, { file: receipt });
    } else {
      const formData = new FormData();
      formData.append('file', receipt);
      formData.append('_method', 'PUT');
      return client.post(`/user-requests/${id}/attachments/receipt`, formData);
    }
  },
  serviceWeapons: (code: ServiceSupportedCustomWeaponList): Promise<DataResponse<ResponseUserWeapons>> => {
    return client.get(`/user-requests/${code}/weapons`);
  },
  servicePermits: (code: MergedServiceCodes['VALIDITY_PERIOD_PROLONGATION']): Promise<DataResponse<ERZPermit[]>> => {
    return client.get(`/user-requests/${code}/permits`);
  },
  getUserRequest: (id: UserRequest['id']): Promise<DataResponse<Array<{ artifacts: string[] }>>> => {
    return client.get(`/user-requests?id=${id}`);
  },
  verifySubmitAvailability: (
    serviceCode: RMPServiceCode,
    missing_person: PayloadVerifySubmitAvailability,
  ): Promise<DataResponse<ResponseVerifySubmitAvailability>> => {
    return client.post(`/user-requests/${serviceCode}/canSubmit`, {
      missing_person,
    });
  },
  hasActiveRequest: (
    serviceCode: MergedServiceCodes['EXTENDED_CRIMINAL_RECORD_EXTRACT'],
  ): Promise<DataResponse<Record<'answer', boolean>>> => client.get(`/user-requests/${serviceCode}/hasActiveRequest`),
  checkAction: (args: {
    id: UserRequest['id'];
    action: string;
    type_id: string;
  }): Promise<{ is_permitted: boolean; reason: Nullable<string> }> => {
    const { id, action, type_id } = args;
    return client.post(`/user-requests/${id}/check-action`, {
      action,
      type_id,
    });
  },
  saveAttachment: (args: SaveAttachmentParams): Promise<DataResponse<SavedAttachment>> => {
    const { request_id, file, form_field, document_type = null } = args;
    const normalizedDocumentType = document_type && document_type !== 'null' ? document_type : null;

    const formData = new FormData();

    formData.append('file', file);
    form_field && formData.append('form_field', form_field);
    normalizedDocumentType && formData.append('document_type', normalizedDocumentType);

    return client.post(`/user-requests/${request_id}/attachments`, formData);
  },
  deleteAttachments: (args: DeleteRequestParams): Promise<void> => {
    const { request_id, ids } = args;

    return client.delete(`/user-requests/${request_id}/attachments`, { ids });
  },
  // TODO: change request according to back
  getPaymentDetails: <T = DataResponse<ResponsePaymentDetails>>(id: UserRequest['id'], asFile = false): Promise<T> => {
    const headers = new Headers();
    if (asFile) {
      headers.set('Accept', 'application/pdf');
      headers.set('Content-Type', 'application/pdf');
    } else {
      headers.set('Accept', 'application/json');
      headers.set('Content-Type', 'application/json');
    }
    return client.get(`/user-requests/${id}/payments/info`, { headers });
  },

  draft: {
    getAll: (): Promise<DataResponse<Nullable<UserRequest[]>>> => {
      return client.get(`/user-requests/drafts`);
    },
    get: (serviceCode: ServiceCode): Promise<DataResponse<[UserRequest]>> => {
      return client.get(`/user-requests/drafts?code=${serviceCode}`);
    },
    getDetails: (draftId: UserRequest['id']): Promise<DataResponse<UserRequestDraft>> => {
      return client.get(`/user-requests/${draftId}`);
    },
    createInvoice: (draftId: UserRequest['id']): Promise<Record<'redirect_uri', string>> => {
      // Hint: returns redirect URL to the GERC (payment system)
      return client.post(`/user-requests/${draftId}/payments/invoice`);
    },
    create: (
      serviceDetails: PayloadCreateDraft,
      defaultMetaData: Pick<UserRequestDraft, 'meta'>,
      parentId?: string,
    ): Promise<ResponseCreateDraft> => {
      return client.post('/user-requests', { service: serviceDetails, ...defaultMetaData, parentId });
    },
    update: ({ draftId, serviceDraft }: PayloadUpdateDraft): Promise<UserRequestDraft> => {
      return client.post(`/user-requests/${draftId}`, serviceDraft);
    },
    delete: (draftId: UserRequest['id']): Promise<void> => {
      return client.delete(`/user-requests/${draftId}`);
    },
  },
};
